import { AnimationPlayer } from '@components/AnimationPlayer'
import Button from '@components/Button'
import Prompt from '@components/Prompt'
import { Titlebar } from '@components/Titlebar/Titlebar'
import { TokenCard } from '@modules/Swap/ExchangeView'
import {
  Box,
  Divider,
  IconButton,
  List,
  ListItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material'
import useBridgeOdin, { ODIN_FEE } from '@plug-core/hooks/useBridgeOdin'
import { logger } from '@plug-core/logger'
import { formatNumber } from '@plug-core/utils/formatNumber'
import React, { PropsWithChildren, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { HiInformationCircle } from 'react-icons/hi'
import { useNavigate } from 'react-router-dom'
import successAnimation from '@assets/lottie/success.json'
import { SwapProvider } from '@plug-core/providers/SwapProvider'
import { BTC_ID, CKBTC_ICP_ID } from '@plug-core/constants/tokenIds'
import { HiArrowUpRight } from 'react-icons/hi2'
import { ROUTES } from '@utils/routes'
import BottomSheet from '@components/BottomSheet'

const BridgeListItem = ({
  attribute,
  children,
}: PropsWithChildren & {
  attribute: string
}) => (
  <ListItem
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      my: 1,
    }}
  >
    <Typography variant="body2">{attribute}</Typography>
    <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5}>
      <Typography variant="body2" color="text.secondary">
        {children}
      </Typography>
    </Stack>
  </ListItem>
)

const BridgeOdin = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [prompt, setPrompt] = useState<{
    title: string
    description: string
  } | null>(null)

  const {
    inputAmount,
    setInputAmount,
    error,
    bridgeInProgress,
    performBridge,
    bridgeMode,
    setBridgeMode,
    token,
    setToken,
    maxAmount,
    receiveAmount,
    icpToken,
    ckBTCToken,
    success,
    slippage,
  } = useBridgeOdin()

  const onInfoClick = (event: any) => {
    event.stopPropagation()

    setPrompt({
      title: t(`defi.odin.titleAbout`),
      description: t(`defi.odin.about`),
    })
  }

  const bridge = async () => {
    try {
      await performBridge()
      setTimeout(() => navigate(-1), 3000)
    } catch (e) {
      logger.error('Failed to bridge odin', { originalError: e, bridgeMode })

      setPrompt({
        title: t(`defi.odin.errors.${bridgeMode}Failed`),
        description: t(`defi.odin.errors.bridgeFailedDescription`),
      })
    }
  }

  return (
    <Stack pt={2} pb={1}>
      <Titlebar
        title={t('defi.odin.title')}
        onActionClick={{
          type: 'back',
          action: () => navigate(-1),
        }}
        leftItem={
          <IconButton sx={{ width: 40, marginRight: 1 }} color="primary" onClick={onInfoClick}>
            <HiInformationCircle size={40} />
          </IconButton>
        }
      />

      <Box borderBottom={1} borderColor="divider" mb={2} mt={0.75} mx={2.5}>
        <Tabs
          value={bridgeMode === 'deposit' ? 0 : 1}
          onChange={(_e, index) => setBridgeMode(index === 0 ? 'deposit' : 'withdraw')}
        >
          <Tab label={t('defi.odin.deposit')} sx={{ width: '50%' }} />
          <Tab label={t('defi.odin.withdraw')} sx={{ width: '50%' }} />
        </Tabs>
      </Box>

      <Stack mx={2} spacing={1.25}>
        <TokenCard
          token={token}
          amount={inputAmount}
          setAmount={setInputAmount}
          mode="stake"
          error={undefined}
          showBalance
          maxAmount={maxAmount}
          tokensForSelect={
            bridgeMode === 'deposit' ? [icpToken.token!, ckBTCToken.token!] : undefined
          }
          disableTokenPicker={bridgeMode !== 'deposit'}
          onTokenSelect={setToken}
        />

        <List component={Paper} disablePadding>
          <BridgeListItem attribute={t('defi.odin.receiveAmount')}>
            {formatNumber(receiveAmount)} {bridgeMode === 'deposit' ? 'BTC' : 'ckBTC'}
          </BridgeListItem>

          {typeof slippage === 'number' && (
            <BridgeListItem attribute={t('defi.odin.slippage')}>
              {formatNumber(slippage)}%
            </BridgeListItem>
          )}

          <BridgeListItem attribute={t('defi.odin.fee')}>{ODIN_FEE} BTC</BridgeListItem>

          <Divider sx={{ borderColor: 'background.default' }} />
        </List>
        <Button
          variant="rainbow"
          disabled={!!error || bridgeInProgress}
          loading={bridgeInProgress}
          onClick={bridge}
          fullWidth
        >
          {error || bridgeMode}
        </Button>

        {token?.id === CKBTC_ICP_ID && (
          <Button
            variant="link"
            startIcon={<HiArrowUpRight />}
            onClick={() => navigate(ROUTES.MINT_CK(BTC_ID))}
            fullWidth
            size="small"
            style={{ fontSize: 13 }}
          >
            No ckBTC? Mint {token.symbol}
          </Button>
        )}
      </Stack>

      <Prompt show={!!prompt} close={() => setPrompt(null)}>
        <Stack alignItems="center">
          <Stack alignItems="center" mb={0.5}>
            <Typography variant="h6" mt={0.2}>
              {prompt?.title}
            </Typography>
            <Typography
              textAlign="center"
              mt={1}
              mb={2}
              color="text.light"
              sx={{ whiteSpace: 'pre-line' }}
            >
              {prompt?.description}
            </Typography>
          </Stack>

          <Button onClick={() => setPrompt(null)} variant="outlined">
            {t('common.understood')}
          </Button>
        </Stack>
      </Prompt>

      <BottomSheet visible={success}>
        <Stack>
          <Typography variant="h6" textAlign="center" mt={2}>
            {t('defi.odin.success')}
          </Typography>
          <AnimationPlayer animationData={successAnimation} />
        </Stack>
      </BottomSheet>
    </Stack>
  )
}

const BridgeOdinWrapped = () => (
  <SwapProvider>
    <BridgeOdin />
  </SwapProvider>
)

export { BridgeOdinWrapped as BridgeOdin }
